/**
 * Dane masowe dla Iveco Tourist Line, zależne od kodu wersji pojazdu ({{version}}).
 * Źródło: iveco-table.html (surowa tabela dostarczona przez klienta, w tym samym folderze).
 *
 * Z tabeli wyliczane są wyłącznie 3 wartości (Sekcja 13 - Mass in running order):
 * - {{massInRunningOrder}} = kolumna "Masa gotowa do jazdy" (wartość max)
 * - {{massAxle2}}          = kolumna "II oś" (wartość max)
 * - {{massAxle1}}          = {{massInRunningOrder}} - {{massAxle2}} (wyliczane)
 *
 * Wartości min (dla obu osi i masy) są przechowywane wyłącznie jako zakres
 * referencyjny do wyświetlenia w tooltipie - nie są wpisywane do formularza.
 *
 * Typy współdzielone między zabudowami (MassDataEntry, StandingPassengersKey,
 * VersionMassDataTable) oraz funkcja odczytująca dane (getMassData) mieszkają
 * w vehicle-mass-data.ts (ten sam folder) - ten plik zawiera wyłącznie dane Iveco.
 *
 * @module iveco-mass-data
 */

import type { VersionMassDataTable } from "./vehicle-mass-data";

/**
 * UWAGA: trzy wiersze źródłowej tabeli (72W04R0L0, 72W04R1L0, 72W04R1L1) mają
 * po dwie wartości rozdzielone "/" bez osobnej kolumny liczby stojących
 * pasażerów. Przyjęto - na podstawie jednoznacznie potwierdzonego wzorca
 * z wiersza 72W02R0L0 (gdzie mniejsza wartość odpowiadała 1 stojącemu
 * pasażerowi, a większa 0 stojącym) - że ta sama zasada obowiązuje tutaj:
 * mniejsza wartość w parze = "1", większa = "0".
 * DO WERYFIKACJI po uzupełnieniu w iveco-table.html kolumny
 * "Number of standing passengers".
 */
export const IVECO_MASS_DATA: VersionMassDataTable = {
  "72W01R0L0": {
    "*": {
      axle1RangeMin: 1577,
      axle1RangeMax: 2220,
      axle2Min: 2768,
      axle2Max: 3418,
      massInRunningOrderMin: 4345,
      massInRunningOrderMax: 4995,
    },
  },
  "72W02R0L0": {
    // Dwa wiersze źródłowe różniły się tylko "I oś max" (2220 / 2353) -
    // "II oś max" i "Masa max" (jedyne używane w obliczeniach) są identyczne,
    // więc liczba stojących pasażerów nie wpływa tu na wynik.
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2353,
      axle2Min: 2500,
      axle2Max: 3282,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 4853,
    },
  },
  "72W04R0L0": {
    "1": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2137,
      axle2Min: 2500,
      axle2Max: 3550,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5137,
    },
    "0": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2153,
      axle2Min: 2500,
      axle2Max: 3605,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5208,
    },
  },
  "72W04R1L0": {
    "1": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2174,
      axle2Min: 2500,
      axle2Max: 3413,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5037,
    },
    "0": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2190,
      axle2Min: 2500,
      axle2Max: 3468,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5108,
    },
  },
  "72W04R1L1": {
    "1": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2174,
      axle2Min: 2500,
      axle2Max: 3412,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5035,
    },
    "0": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2189,
      axle2Min: 2500,
      axle2Max: 3467,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5106,
    },
  },
  "82W02R0L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2400,
      axle2Min: 2500,
      axle2Max: 3732,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5853,
    },
  },
  "82W02R1L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2400,
      axle2Min: 2500,
      axle2Max: 3589,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5753,
    },
  },
  "82W02R2L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2400,
      axle2Min: 2500,
      axle2Max: 3529,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5703,
    },
  },
  "82W02R3L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2400,
      axle2Min: 2500,
      axle2Max: 3400,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5600,
    },
  },
  "82W02R0L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2295,
      axle2Min: 2500,
      axle2Max: 3577,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5573,
    },
  },
  "82W02R1L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2339,
      axle2Min: 2500,
      axle2Max: 3433,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5473,
    },
  },
  "82W02R2L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2348,
      axle2Min: 2500,
      axle2Max: 3374,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5423,
    },
  },
  "82W02R3L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2378,
      axle2Min: 2500,
      axle2Max: 3245,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5323,
    },
  },
  "82W03R0L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2300,
      axle2Min: 2500,
      axle2Max: 3473,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5711,
    },
  },
  "82W03R1L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2300,
      axle2Min: 2500,
      axle2Max: 3331,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5611,
    },
  },
  "82W03R2L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2300,
      axle2Min: 2500,
      axle2Max: 3274,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5561,
    },
  },
  "82W03R3L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2300,
      axle2Min: 2500,
      axle2Max: 3147,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5447,
    },
  },
  "82W03R0L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2212,
      axle2Min: 2500,
      axle2Max: 3519,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5431,
    },
  },
  "82W03R1L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2255,
      axle2Min: 2500,
      axle2Max: 3376,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5331,
    },
  },
  "82W03R2L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2261,
      axle2Min: 2500,
      axle2Max: 3320,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5281,
    },
  },
  "82W03R3L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2288,
      axle2Min: 2500,
      axle2Max: 3192,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5181,
    },
  },
  // Wersje z tonażem "8X" (dowolna dziesiątka 80-89) - patrz getIvecoMassData
  "8XW05R0L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2147,
      axle2Min: 2500,
      axle2Max: 3559,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5609,
    },
  },
  "8XW06R0L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2062,
      axle2Min: 2500,
      axle2Max: 3635,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5617,
    },
  },
  "8XW07R0L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2146,
      axle2Min: 2500,
      axle2Max: 3155,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5201,
    },
  },
  "8XW08R0L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2205,
      axle2Min: 2500,
      axle2Max: 3535,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5640,
    },
  },
  "8XW08R0L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2079,
      axle2Min: 2500,
      axle2Max: 3381,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5360,
    },
  },
  "8XW08R1L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2256,
      axle2Min: 2500,
      axle2Max: 3384,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5540,
    },
  },
  "8XW08R1L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2130,
      axle2Min: 2500,
      axle2Max: 3230,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5260,
    },
  },
  "8XW08R2L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2267,
      axle2Min: 2500,
      axle2Max: 3323,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5490,
    },
  },
  "8XW08R2L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2142,
      axle2Min: 2500,
      axle2Max: 3168,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5210,
    },
  },
  "8XW08R3L0": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2300,
      axle2Min: 2500,
      axle2Max: 3187,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5390,
    },
  },
  "8XW08R3L1": {
    "*": {
      axle1RangeMin: 1500,
      axle1RangeMax: 2178,
      axle2Min: 2500,
      axle2Max: 3032,
      massInRunningOrderMin: 4000,
      massInRunningOrderMax: 5110,
    },
  },
};

/**
 * Dla tonażu zaczynającego się od "8" (np. "80".."89") tabela źródłowa koduje
 * dziesiątkę tonażu wieloznacznikiem "8X" (patrz komentarz przy definicji
 * IVECO_MASS_DATA) - ta funkcja generuje dodatkowy kandydujący klucz wersji
 * z podmienionym tonażem, do użycia przez getMassData (vehicle-mass-data.ts)
 * jako fallback, gdy dokładna wersja nie ma dopasowania.
 *
 * @param version - Kod wersji pojazdu (np. "82W02R0L0")
 * @returns Lista kandydujących kluczy wersji do przeszukania, w kolejności priorytetu
 */
export function expandIvecoVersionCandidates(version: string): string[] {
  const candidates = [version];
  if (version[0] === "8" && version.length > 2) {
    candidates.push(`8X${version.slice(2)}`);
  }
  return candidates;
}
